/**
 * Add Broker Tab Component
 * 
 * Form for adding a new broker platform to the system.
 * 
 * Features:
 * - Comprehensive form with validation
 * - Support for broker metadata and login configuration
 * - Design matches AddEditTradeAccount page
 * - Real-time validation and feedback
 * 
 * @component
 */

import React, { useState } from 'react';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { FiSave, FiX, FiInfo } from 'react-icons/fi';

const AddBrokerTab = ({ onBrokerSaved, onCancel }) => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    broker_name: '',
    display_name: '',
    country_code: '',
    country_name: '',
    currency: 'USD',
    is_active: true,
    login_base_url: '',
    requires_iframe: false,
    allows_api_key: true,
    token_refresh_seconds: 86400,
  });
  const [errors, setErrors] = useState({});

  /**
   * Handle form field changes
   */
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  /**
   * Validate form data
   */
  const validateForm = () => {
    const newErrors = {};

    if (!formData.broker_name?.trim()) {
      newErrors.broker_name = 'Broker name is required';
    }
    if (!formData.display_name?.trim()) {
      newErrors.display_name = 'Display name is required';
    }
    if (!formData.country_code?.trim()) {
      newErrors.country_code = 'Country code is required';
    } else if (formData.country_code.length !== 2) {
      newErrors.country_code = 'Country code must be 2 characters (ISO 3166-1 alpha-2)';
    }
    if (!formData.country_name?.trim()) {
      newErrors.country_name = 'Country name is required';
    }
    if (!formData.currency?.trim()) {
      newErrors.currency = 'Currency is required';
    } else if (formData.currency.length !== 3) {
      newErrors.currency = 'Currency must be 3 characters (ISO 4217)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showSnackbar('Please fix validation errors', 'error');
      return;
    }

    try {
      setLoading(true);

      // Prepare broker data with proper structure
      const brokerData = {
        broker_name: formData.broker_name.trim(),
        display_name: formData.display_name.trim(),
        country_code: formData.country_code.trim().toUpperCase(),
        country_name: formData.country_name.trim(),
        currency: formData.currency.trim().toUpperCase(),
        is_active: formData.is_active,
        login_config: {
          requires_iframe: formData.requires_iframe,
          allows_api_key: formData.allows_api_key,
          token_refresh_seconds: Math.max(60, parseInt(formData.token_refresh_seconds || 86400, 10)),
          ...(formData.login_base_url && { login_base_url: formData.login_base_url.trim() })
        }
      };

      await services.brokerPlatforms.createBroker(brokerData);
      onBrokerSaved('Broker created successfully');

    } catch (error) {
      console.error('Error creating broker:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create broker';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Info Box */}
        <div className="bg-[var(--badge-info-bg)] border border-accent/30 rounded-lg p-4 flex items-start gap-3">
          <FiInfo className="text-[var(--badge-info-fg)] text-xl flex-shrink-0 mt-0.5" />
          <div className="text-sm text-fg-2">
            <p className="font-semibold mb-1">Adding a New Broker Platform</p>
            <p>Fill in the broker details below. All fields marked with * are required.</p>
          </div>
        </div>

        {/* Basic Information Section */}
        <div className="bg-bg-2 rounded-lg border border-[var(--divider)] p-6">
          <h3 className="ds-h3 text-fg-1 mb-4">
            Basic Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Broker Name */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Broker Name (Internal ID) *
              </label>
              <input
                type="text"
                name="broker_name"
                value={formData.broker_name}
                onChange={handleChange}
                placeholder="e.g., zerodha, icici"
                className={`ds-input ${errors.broker_name ? 'ds-input-error' : ''}`}
              />
              {errors.broker_name && (
                <p className="mt-1 text-sm text-loss">{errors.broker_name}</p>
              )}
            </div>

            {/* Display Name */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Display Name *
              </label>
              <input
                type="text"
                name="display_name"
                value={formData.display_name}
                onChange={handleChange}
                placeholder="e.g., Zerodha, ICICI Direct"
                className={`ds-input ${errors.display_name ? 'ds-input-error' : ''}`}
              />
              {errors.display_name && (
                <p className="mt-1 text-sm text-loss">{errors.display_name}</p>
              )}
            </div>

            {/* Country Code */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Country Code (ISO 3166-1 alpha-2) *
              </label>
              <input
                type="text"
                name="country_code"
                value={formData.country_code}
                onChange={handleChange}
                placeholder="e.g., IN, US"
                maxLength={2}
                className={`ds-input uppercase ${errors.country_code ? 'ds-input-error' : ''}`}
              />
              {errors.country_code && (
                <p className="mt-1 text-sm text-loss">{errors.country_code}</p>
              )}
            </div>

            {/* Country Name */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Country Name *
              </label>
              <input
                type="text"
                name="country_name"
                value={formData.country_name}
                onChange={handleChange}
                placeholder="e.g., India, United States"
                className={`ds-input ${errors.country_name ? 'ds-input-error' : ''}`}
              />
              {errors.country_name && (
                <p className="mt-1 text-sm text-loss">{errors.country_name}</p>
              )}
            </div>

            {/* Currency */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Currency (ISO 4217) *
              </label>
              <input
                type="text"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                placeholder="e.g., INR, USD"
                maxLength={3}
                className={`ds-input uppercase ${errors.currency ? 'ds-input-error' : ''}`}
              />
              {errors.currency && (
                <p className="mt-1 text-sm text-loss">{errors.currency}</p>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-accent bg-bg-3 border-[var(--divider)] rounded focus:ring-accent"
              />
              <label htmlFor="is_active" className="text-sm font-medium text-fg-2">
                Broker is Active
              </label>
            </div>
          </div>
        </div>

        {/* Login Configuration Section */}
        <div className="bg-bg-2 rounded-lg border border-[var(--divider)] p-6">
          <h3 className="ds-h3 text-fg-1 mb-4">
            Login Configuration
          </h3>
          <div className="space-y-4">
            {/* Login Base URL */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Login Base URL (Optional)
              </label>
              <input
                type="url"
                name="login_base_url"
                value={formData.login_base_url}
                onChange={handleChange}
                placeholder="https://broker-login-url.com"
                className="ds-input"
              />
            </div>

            {/* Token Refresh Seconds */}
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-2">
                Token Refresh Interval (seconds)
              </label>
              <input
                type="number"
                name="token_refresh_seconds"
                value={formData.token_refresh_seconds}
                onChange={handleChange}
                min="60"
                className="ds-input"
              />
            </div>

            {/* Checkboxes for Login Options */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="requires_iframe"
                  name="requires_iframe"
                  checked={formData.requires_iframe}
                  onChange={handleChange}
                  className="w-4 h-4 text-accent bg-bg-3 border-[var(--divider)] rounded focus:ring-accent"
                />
                <label htmlFor="requires_iframe" className="text-sm text-fg-2">
                  Requires iFrame for Login
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="allows_api_key"
                  name="allows_api_key"
                  checked={formData.allows_api_key}
                  onChange={handleChange}
                  className="w-4 h-4 text-accent bg-bg-3 border-[var(--divider)] rounded focus:ring-accent"
                />
                <label htmlFor="allows_api_key" className="text-sm text-fg-2">
                  Allows API Key Authentication
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex gap-4 justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="btn btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiX className="inline mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
                Creating...
              </>
            ) : (
              <>
                <FiSave />
                Create Broker
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBrokerTab;
